import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Sparkles, Camera, PencilLine, Star, Plus, ArrowRight } from 'lucide-react';
import { MEALS, MEAL_SPLIT } from './trackingConstants';

const SourceIcon = ({ source }) => {
  if (source === 'ai') return <Sparkles className="w-3 h-3 text-primary" />;
  if (source === 'photo') return <Camera className="w-3 h-3 text-yellow-400" />;
  if (source === 'manual') return <PencilLine className="w-3 h-3 text-zinc-500" />;
  return null;
};

/* Anneau miniature de progression pour l'icône de repas */
const MiniRing = ({ pct, children }) => {
  const size = 40, stroke = 3, r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(pct || 0, 100) / 100);
  return (
    <div className="relative w-10 h-10 shrink-0">
      <svg width={size} height={size} className="-rotate-90 absolute inset-0">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgb(var(--c-white) / 0.08)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--primary, #E91E63)" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
};

/* Carte repas compacte façon Yazio : icône ronde, kcal/objectif, aperçu des aliments, bouton + */
const MealCard = ({ meal, items, goalCalories, onQuickAdd, onDelete, onFavorite, en }) => {
  const [expanded, setExpanded] = useState(false);
  const kcal = items.reduce((s, e) => s + (e.calories || 0), 0);
  const target = Math.round((goalCalories || 2000) * (MEAL_SPLIT[meal.key] || 0.2));
  const pct = target ? (kcal / target) * 100 : 0;
  const preview = items.map((e) => e.food_name).join(', ');

  return (
    <div className="rounded-3xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] hover:border-white/20 transition-colors overflow-hidden" data-testid={`meal-card-${meal.key}`}>
      <button onClick={() => setExpanded((v) => !v)} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`meal-card-toggle-${meal.key}`}>
        <MiniRing pct={pct}><meal.icon className="w-4 h-4 text-primary" /></MiniRing>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold text-white text-sm flex items-center gap-1">
              {en ? meal.en : meal.fr}
              <ArrowRight className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </p>
            <span className="text-sm font-bold text-white tabular-nums shrink-0">{Math.round(kcal)} <span className="text-zinc-500 font-medium">/ {target} kcal</span></span>
          </div>
          <p className="text-xs text-zinc-500 truncate mt-0.5">{preview || (en ? 'Nothing logged yet' : 'Rien d\'enregistré')}</p>
        </div>
        <span
          role="button" tabIndex={0}
          onClick={(e) => { e.stopPropagation(); onQuickAdd(meal.key); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onQuickAdd(meal.key); } }}
          className="w-10 h-10 rounded-full bg-white text-primary hover:bg-primary hover:text-white transition-all flex items-center justify-center shrink-0 shadow-md"
          data-testid={`meal-add-button-${meal.key}`}
        >
          <Plus className="w-5 h-5" strokeWidth={2.5} />
        </span>
      </button>
      <AnimatePresence>
        {expanded && items.length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="divide-y divide-white/5 border-t border-white/5">
              {items.map((e) => (
                <div key={e.entry_id} className="flex items-center justify-between px-4 py-2.5 group" data-testid={`food-entry-${e.entry_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium flex items-center gap-1.5 min-w-0">
                      <SourceIcon source={e.source} /> <span className="truncate">{e.food_name}</span>
                    </p>
                    <p className="text-xs text-zinc-500 truncate">
                      {e.quantity} {e.unit} · P {Math.round(e.protein)}g · G {Math.round(e.carbs)}g · L {Math.round(e.fat)}g
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-sm font-bold text-white tabular-nums">{Math.round(e.calories)}</span>
                    <button onClick={() => onFavorite(e)} className="p-1.5 rounded-lg text-zinc-600 hover:text-yellow-400 hover:bg-yellow-500/10 transition-colors" title={en ? 'Add to favorites' : 'Ajouter aux favoris'} data-testid={`favorite-entry-btn-${e.entry_id}`}>
                      <Star className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => onDelete(e.entry_id)} className="p-1.5 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`delete-entry-btn-${e.entry_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const MealList = ({ entries, onDelete, onFavorite, onQuickAdd, goalCalories, en }) => {
  const byMeal = MEALS.reduce((acc, m) => ({ ...acc, [m.key]: [] }), {});
  (entries || []).forEach((e) => { (byMeal[e.meal] || byMeal.breakfast).push(e); });

  return (
    <div className="space-y-3" data-testid="meal-list">
      {MEALS.map((meal) => (
        <MealCard
          key={meal.key} meal={meal} items={byMeal[meal.key] || []} goalCalories={goalCalories}
          onQuickAdd={onQuickAdd} onDelete={onDelete} onFavorite={onFavorite} en={en}
        />
      ))}
    </div>
  );
};

export default MealList;
